<template>
  <div>
    <v-container>
      <v-card class="tn-grid">
        <div class="tn-grid__head">
          <div class="tn-grid__heading">
            <span class="tn-grid__icon"><v-icon size="22">mdi-cellphone-text</v-icon></span>
            <div class="tn-grid__titles">
              <h2 class="tn-grid__title">App content</h2>
              <div class="tn-grid__count">{{ current.hint }}</div>
            </div>
          </div>
          <div class="tn-grid__tools">
            <v-btn depressed color="primary" @click="openAdd">
              <v-icon left size="20">mdi-plus</v-icon>
              {{ current.add }}
            </v-btn>
          </div>
        </div>

        <v-tabs v-model="tab" color="primary" class="px-4" show-arrows>
          <v-tab v-for="place in placements" :key="place.value">
            {{ place.label }}
            <span class="tn-app-content__count">{{ itemsOf(place.value).length }}</span>
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>

        <v-data-table
          :headers="tableHeaders"
          :items="itemsOf(current.value)"
          item-key="id"
          :loading="loading"
          loading-text="Loading… Please wait"
          :items-per-page="-1"
          hide-default-footer
          :mobile-breakpoint="0"
          class="tn-grid__table"
        >
          <template v-slot:item.image_url="{ item }">
            <img v-if="item.image_url && !broken[item.id]" :src="item.image_url" alt="" class="tn-app-content__thumb" @error="$set(broken, item.id, true)">
            <v-icon v-else color="error" title="Image file missing on the server">mdi-image-broken-variant</v-icon>
          </template>
          <template v-slot:item.title="{ item }">
            <div class="font-weight-medium">{{ item.title || '—' }}</div>
            <div v-if="item.subtitle" class="tn-app-content__sub">{{ item.subtitle }}</div>
          </template>
          <template v-slot:item.action="{ item }">
            <span v-if="item.action_type === 'none'" class="tn-app-content__sub">Not tappable</span>
            <span v-else>
              <span v-if="item.button_text" class="font-weight-medium">{{ item.button_text }} → </span>
              <code>{{ item.action_value }}</code>
            </span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :class="['tn-status-chip', 'tn-status-chip--' + status(item).color]">{{ status(item).label }}</v-chip>
            <div v-if="item.starts_at || item.ends_at" class="tn-app-content__sub mt-1">
              {{ item.starts_at || '…' }} → {{ item.ends_at || '…' }}
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" :aria-label="'Edit ' + (item.title || 'item')" @click="openEdit(item)">
              <v-icon size="20">mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon small color="error" :aria-label="'Delete ' + (item.title || 'item')" @click="remove(item)">
              <v-icon size="20">mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <template v-slot:no-data>
            <div class="tn-empty">
              <v-icon size="40">mdi-image-off-outline</v-icon>
              <span>{{ current.empty }}</span>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- One dialog for add and edit: it always opens with the item it is for (or empty). -->
    <v-dialog v-model="dialog" max-width="600" scrollable :persistent="saving">
      <v-card>
        <v-card-title class="text-h6">{{ (isEdit ? 'Edit ' : 'Add ') + current.noun }}</v-card-title>
        <v-card-text class="pt-2">
          <v-form ref="form" @submit.prevent="save">
            <image-field v-model="form.image" :current="currentImage" label="Image" />
            <div v-if="!isEdit && !form.image" class="tn-app-content__hint">{{ current.imageHint }}</div>

            <v-text-field
              v-model="form.title"
              label="Title"
              outlined
              counter="255"
              :rules="[v => !isOnboarding || !!(v && v.trim()) || 'Title is required', v => !v || v.length <= 255 || 'At most 255 characters']"
            ></v-text-field>
            <v-textarea
              v-model="form.subtitle"
              :label="isOnboarding ? 'Text' : 'Subtitle'"
              outlined
              rows="2"
              auto-grow
              counter="255"
              :rules="[v => !isOnboarding || !!(v && v.trim()) || 'Text is required', v => !v || v.length <= 255 || 'At most 255 characters']"
            ></v-textarea>

            <template v-if="!isOnboarding">
              <div class="tn-form-section">When tapped</div>
              <v-select
                v-model="form.action_type"
                :items="actionTypes"
                label="Action"
                outlined
              ></v-select>
              <v-combobox
                v-if="form.action_type === 'screen'"
                v-model="form.action_value"
                :items="screens"
                label="Screen"
                hint="A section, or one item: trip/{slug}, activity/{id}, cruise/{id}"
                persistent-hint
                outlined
                :rules="[v => !!(v && String(v).trim()) || 'Choose a screen']"
              ></v-combobox>
              <v-text-field
                v-if="form.action_type === 'url'"
                v-model="form.action_value"
                label="Link"
                placeholder="https://tanefer.com/…"
                outlined
                :rules="[v => /^https?:\/\/\S+$/i.test(v || '') || 'A full link starting with https://']"
              ></v-text-field>
              <v-text-field
                v-if="form.action_type !== 'none'"
                v-model="form.button_text"
                label="Button text"
                outlined
                counter="50"
                class="mt-2"
                :rules="[v => !v || v.length <= 50 || 'At most 50 characters']"
              ></v-text-field>
            </template>

            <div class="tn-form-section">Display</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="form.sort"
                  label="Order"
                  type="number"
                  min="0"
                  hint="Smaller first"
                  persistent-hint
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-switch v-model="form.is_active" inset label="Show in the app" class="mt-2"></v-switch>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.starts_at" label="From (optional)" type="date" outlined clearable></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.ends_at"
                  label="Until (optional)"
                  type="date"
                  outlined
                  clearable
                  :rules="[v => !v || !form.starts_at || v >= form.starts_at || 'Must be on or after the start']"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn text :disabled="saving" @click="dialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed color="primary" :loading="saving" @click="save">{{ isEdit ? 'Save changes' : 'Add' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { appBanners, updateAppBanner, appBanner, headers } from '../../links'
import ImageField from '../../components/ImageField.vue'
import Swal from 'sweetalert2'

const placements = [
  {
    value: 'home_hero',
    label: 'Home hero',
    noun: 'hero banner',
    add: 'Add hero banner',
    hint: 'Top of the app Home screen. Several banners show as a carousel.',
    imageHint: 'The image already holds the text and button (wide image, about 2:1).',
    empty: 'No hero banner: the app hides it.'
  },
  {
    value: 'home_promo',
    label: 'Home promo',
    noun: 'promo card',
    add: 'Add promo card',
    hint: 'The "TaNefer Private Signatures" card: the app writes the title, subtitle and button over the image.',
    imageHint: 'Keep the image calm: the app writes the texts over it.',
    empty: 'No promo card: the app hides it.'
  },
  {
    value: 'onboarding',
    label: 'Onboarding',
    noun: 'onboarding page',
    add: 'Add onboarding page',
    hint: 'The pages shown the first time the app opens, in order. None: the app shows its own pages.',
    imageHint: 'A tall photo (portrait) for a full phone screen.',
    empty: 'No onboarding pages: the app shows its own pages.'
  }
]

function emptyForm (placement) {
  return { placement, image: null, title: '', subtitle: '', action_type: 'none', action_value: '', button_text: '', sort: 0, is_active: true, starts_at: '', ends_at: '' }
}

export default {
  components: { ImageField },
  data () {
    return {
      placements,
      tab: 0,
      items: [],
      broken: {}, // ids whose image could not be loaded
      loading: true,
      actionTypes: [
        { text: 'Nothing (not tappable)', value: 'none' },
        { text: 'Open an app screen', value: 'screen' },
        { text: 'Open a link in the browser', value: 'url' }
      ],
      screens: ['hotels', 'trips', 'cruises', 'activities', 'build_trip', 'flights'],
      dialog: false,
      isEdit: false,
      id: null,
      form: emptyForm('home_hero'),
      currentImage: null,
      saving: false
    }
  },
  computed: {
    current () {
      return this.placements[this.tab] || this.placements[0]
    },
    isOnboarding () {
      return this.form.placement === 'onboarding'
    },
    tableHeaders () {
      return [
        { text: 'Image', value: 'image_url', sortable: false },
        { text: 'Text', value: 'title', sortable: false },
        // Onboarding pages are not tappable.
        ...(this.current.value === 'onboarding' ? [] : [{ text: 'Tap', value: 'action', sortable: false }]),
        { text: 'Order', value: 'sort' },
        { text: 'Status', value: 'status', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },
  methods: {
    notify (color, text) {
      this.$root.$emit('tn-notify', { color, text })
    },
    itemsOf (placement) {
      return this.items.filter(item => item.placement === placement)
    },
    // `is_live` comes from the server (what the app shows now); the rest says why not.
    status (item) {
      if (item.is_live) return { label: 'Live', color: 'success' }
      if (!item.is_active) return { label: 'Hidden', color: 'grey' }
      if (item.starts_at && item.starts_at > new Date().toISOString().slice(0, 10)) return { label: 'Scheduled', color: 'warning' }
      return { label: 'Ended', color: 'grey' }
    },
    open (isEdit, values, id, image) {
      this.isEdit = isEdit
      this.id = id
      this.form = { ...emptyForm(this.current.value), ...values }
      this.currentImage = image || null
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    openAdd () {
      const next = this.itemsOf(this.current.value).reduce((max, item) => Math.max(max, item.sort), 0) + 1
      this.open(false, { sort: next }, null, null)
    },
    openEdit (item) {
      this.open(true, {
        title: item.title || '',
        subtitle: item.subtitle || '',
        action_type: item.action_type || 'none',
        action_value: item.action_value || '',
        button_text: item.button_text || '',
        sort: item.sort,
        is_active: item.is_active,
        starts_at: item.starts_at || '',
        ends_at: item.ends_at || ''
      }, item.id, item.image_url)
    },
    save () {
      if (!this.$refs.form.validate()) return
      if (!this.isEdit && !this.form.image) {
        this.notify('error', 'Choose an image')
        return
      }
      const formData = new FormData()
      const fields = ['placement', 'title', 'subtitle', 'action_type', 'action_value', 'button_text', 'starts_at', 'ends_at']
      fields.forEach(field => formData.append(field, String(this.form[field] || '').trim()))
      formData.append('sort', Number(this.form.sort) || 0)
      formData.append('is_active', this.form.is_active ? 1 : 0)
      if (this.form.image) formData.append('image', this.form.image, this.form.image.name)

      this.saving = true
      const url = this.isEdit ? updateAppBanner(this.id) : appBanners
      this.$http.post(url, formData, { headers: headers(this.$cookies.get('userToken')) }).then(() => {
        this.saving = false
        this.dialog = false
        this.notify('success', this.isEdit ? 'Saved' : 'Added')
        this.load()
      }, err => {
        this.saving = false
        this.notify('error', this.errorText(err, 'Could not save'))
      })
    },
    remove (item) {
      Swal.fire({
        title: 'Delete this ' + this.current.noun + '?',
        text: 'It disappears from the app at once.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (!result.isConfirmed) return
        this.$http.delete(appBanner(item.id), { headers: headers(this.$cookies.get('userToken')) }).then(() => {
          this.notify('success', 'Deleted')
          this.load()
        }, err => {
          this.notify('error', this.errorText(err, 'Could not delete'))
        })
      })
    },
    // The API answers 422 with { errors: { field: [message] } } and no `message`.
    errorText (err, fallback) {
      const body = (err && err.body) || {}
      const errors = body.errors && Object.values(body.errors)
      if (errors && errors.length) return [].concat(errors[0])[0]
      return body.message || fallback
    },
    load () {
      this.loading = true
      this.$http.get(appBanners, { headers: headers(this.$cookies.get('userToken')) }).then(response => {
        this.loading = false
        this.items = response.body.data || []
      }, err => {
        this.loading = false
        this.notify('error', this.errorText(err, 'Could not load the app content'))
      })
    }
  },
  created () {
    this.load()
  }
}
</script>

<style lang="scss" scoped>
.tn-form-section {
  margin: 4px 0 12px;
  color: var(--tn-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.tn-app-content__thumb {
  display: block;
  width: 96px;
  height: 56px;
  margin: 8px 0;
  border: 1px solid var(--tn-border);
  border-radius: 6px;
  object-fit: cover;
}

.tn-app-content__sub {
  color: var(--tn-muted);
  font-size: 12.5px;
}

.tn-app-content__count {
  margin-left: 6px;
  color: var(--tn-muted);
  font-size: 12px;
}

.tn-app-content__hint {
  margin: -8px 0 16px;
  color: var(--tn-muted);
  font-size: 12.5px;
}
</style>
